// Single source of truth for the PaymentAcceptances/Update body.
//
// The API maps the DTO onto the tracked entity, so any scalar that is missing from
// the body is written back as null (child collections are the exception - they are
// skipped when null). Pages that build their own partial body therefore silently
// wipe fields such as offsets / foreign currency / registration month instead of
// updating them, which is why every caller of PaymentAcceptanceCreateEditModal
// must go through this builder.
//
// Keys whose value is `undefined` are dropped by JSON.stringify, so a page that
// never loads a given field will not blank it out on the server.

// Reporting/registration months are pinned to UTC day 10 (the API re-pins them to
// day 10 as well) so a local-midnight date cannot shift into the previous month.
const toReportingMonthISOString = (date) => {
    if (!date) return null;
    if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}/.test(date)) {
        const [year, month] = date.split('T')[0].split('-').map(Number);
        return new Date(Date.UTC(year, month - 1, 10)).toISOString();
    }
    const parsed = date instanceof Date ? date : new Date(date);
    if (Number.isNaN(parsed.getTime())) return null;
    return new Date(Date.UTC(parsed.getFullYear(), parsed.getMonth(), 10)).toISOString();
};

const toISOStringOrNow = (date) => {
    if (!date) return new Date().toISOString();
    const parsed = date instanceof Date ? date : new Date(date);
    return Number.isNaN(parsed.getTime()) ? new Date().toISOString() : parsed.toISOString();
};

export const buildPaymentAcceptanceUpdatePayload = (item) => {
    if (!item) return null;

    const reportingMonthDate = toReportingMonthISOString(item.reportingMonthDate);

    return {
        id: item.id,
        caseId: item.caseId,
        accountCodeName: item.accountCodeName,
        accountCodeId: item.accountCodeId,
        accountCaseId: item.accountCaseId,
        reference: item.reference,
        amount: item.amount,
        uploadDate: item.uploadDate,
        paymentAcceptanceType: item.paymentAcceptanceType,
        description: item.description,
        withholdingTax: item.withholdingTax,
        checks: item.checks,
        creditCards: item.creditCards,
        cash: item.cash,
        bankTransactions: item.bankTransactions,
        offsets: item.offsets,
        date: toISOStringOrNow(item.date),
        reportingMonthDate: reportingMonthDate ?? toISOStringOrNow(item.reportingMonthDate),
        registrationMonthDate: toReportingMonthISOString(item.registrationMonthDate) ?? reportingMonthDate,
        confirmationNumber: item.confirmationNumber,
        deferredReceipt: item.deferredReceipt,
        foreignCurrencyCode: item.foreignCurrencyCode,
        foreignCurrencyAmount: item.foreignCurrencyAmount,
        exchangeRateType: item.exchangeRateType,
        exchangeRate: item.exchangeRate,
        relatedIncomeId: item.relatedIncomeId,
        relatedInvoiceReference: item.relatedInvoiceReference,
        relatedExpenseId: item.relatedExpenseId,
        relatedJournalEntryId: item.relatedJournalEntryId,
        creditAccount: item.creditAccount,
        debitAccount: item.debitAccount,
        creditAccountAmount: item.creditAccountAmount,
        debitAccountAmount: item.debitAccountAmount,
        // Persist the main PDF when it was promoted off a deleted payment-method line
        // (OCR often stores the file only on check/cash/etc. DocumentUrl).
        receiptDocumentUrl: item.receiptDocumentUrl,
    };
};

export default buildPaymentAcceptanceUpdatePayload;
